import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Home, Award, ClipboardCheck, User, ChevronLeft, LogOut } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HomeView from "@/components/parent/HomeView";
import BepView from "@/components/parent/BepView";
import ChartView from "@/components/parent/ChartView";
import AreaDetailView from "@/components/parent/AreaDetailView";
import TasksView from "@/components/parent/TasksView";
import TaskDetailView from "@/components/parent/TaskDetailView";
import NotesView from "@/components/parent/NotesView";
import ProfileView from "@/components/parent/ProfileView";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const TABS = [
  { key: "home", label: "Ana Sayfa", icon: Home },
  { key: "bep", label: "BEP Planı", icon: Award },
  { key: "tasks", label: "Görevler", icon: ClipboardCheck },
  { key: "profile", label: "Profil", icon: User },
];
const SUB_TITLES = { notes: "Ders Notları", chart: "Gelişim Grafiği", task: "Görev Detayı" };

const isoDate = (d) => d.toISOString().slice(0, 10);
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d; };

function applyParentDeepLink(searchParams, { setTab, setSub }) {
  const tab = searchParams.get("tab");
  const view = searchParams.get("view");
  const task = searchParams.get("task");
  if (task) {
    setTab("tasks");
    setSub({ type: "task", id: task });
    return;
  }
  if (view === "notes") {
    setTab("home");
    setSub("notes");
    return;
  }
  if (view === "chart") {
    setTab("home");
    setSub("chart");
    return;
  }
  if (tab && ["home", "bep", "tasks", "profile"].includes(tab)) {
    setTab(tab);
    setSub(null);
  }
}

export default function ParentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bep, setBep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("home");
  const [sub, setSub] = useState(null); // 'notes' | 'chart' | {type:'task', id}
  const [bepStart, setBepStart] = useState(() => isoDate(monthsAgo(6)));
  const [bepEnd, setBepEnd] = useState(() => isoDate(new Date()));

  useEffect(() => { document.title = "Veli Paneli - Renka"; }, []);

  const loadAll = async () => {
    try {
      const [s, t, n] = await Promise.all([api.get("/parent/students"), api.get("/parent/tasks"), api.get("/parent/notes")]);
      setStudents(s.data); setTasks(t.data); setNotes(n.data);
      setStudentId((prev) => prev || (s.data[0]?.id ?? ""));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyParentDeepLink(searchParams, { setTab, setSub });
  }, [searchParams]);

  useEffect(() => {
    const onReload = () => { loadAll(); };
    const onNav = (e) => {
      const link = e?.detail?.link || "";
      if (!link.startsWith("/veli")) return;
      try {
        const u = new URL(link, window.location.origin);
        applyParentDeepLink(u.searchParams, { setTab, setSub });
      } catch { /* ignore */ }
    };
    window.addEventListener("renka-parent-reload", onReload);
    window.addEventListener("renka-parent-navigate", onNav);
    return () => {
      window.removeEventListener("renka-parent-reload", onReload);
      window.removeEventListener("renka-parent-navigate", onNav);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!studentId) return;
    api.get(`/parent/bep?student_id=${studentId}&start=${bepStart}&end=${bepEnd}`).then((r) => setBep(r.data)).catch(() => setBep(null));
  }, [studentId, bepStart, bepEnd]);

  const student = useMemo(() => students.find((s) => s.id === studentId) || students[0], [students, studentId]);
  const myTasks = useMemo(() => tasks.filter((t) => !studentId || t.student_id === studentId), [tasks, studentId]);
  const myNotes = useMemo(() => notes.filter((n) => !studentId || n.student_id === studentId), [notes, studentId]);

  // Deep-link: görev bildiriminde doğru çocuğu seç
  useEffect(() => {
    if (sub?.type !== "task" || !sub.id || !tasks.length) return;
    const t = tasks.find((x) => x.id === sub.id);
    if (t?.student_id && t.student_id !== studentId) setStudentId(t.student_id);
  }, [sub, tasks, studentId]);

  const onLogout = async () => { await logout(); navigate("/login"); };
  const nav = {
    goTab: (t) => { setTab(t); setSub(null); },
    goSub: (s) => setSub(s),
    back: () => setSub(null),
  };

  const activeTask = sub?.type === "task" ? tasks.find((t) => t.id === sub.id) : null;
  const subTitle = sub ? (sub.type === "area" ? `${sub.name} Hedefleri` : SUB_TITLES[sub.type || sub]) : null;

  const renderContent = () => {
    if (sub === "notes") return <NotesView notes={myNotes} />;
    if (sub === "chart") return <ChartView studentId={studentId} colors={Object.fromEntries((bep?.summary?.areas || []).map((a) => [a.area, a.color]))} />;
    if (sub?.type === "area") return <AreaDetailView areaName={sub.name} bep={bep} />;
    if (sub?.type === "task") {
      if (activeTask) return <TaskDetailView task={activeTask} onReload={loadAll} />;
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
        </div>
      );
    }
    if (tab === "home") return <HomeView user={user} student={student} students={students} onSelectStudent={setStudentId} nav={nav} />;
    if (tab === "bep") return <BepView student={student} bep={bep} nav={nav} range={{ start: bepStart, end: bepEnd }} onRange={(f, v) => (f === "start" ? setBepStart(v) : setBepEnd(v))} />;
    if (tab === "tasks") return <TasksView tasks={myTasks} nav={nav} />;
    if (tab === "profile") return <ProfileView student={student} user={user} onLogout={onLogout} />;
    return null;
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 lg:flex" data-testid="parent-portal">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-shrink-0 flex-col border-r border-slate-200 bg-white p-5 lg:flex">
        <Logo className="h-9 w-auto object-contain" alt="RENKA" />
        <nav className="mt-8 flex-1 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`nav-${t.key}`} onClick={() => nav.goTab(t.key)}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${tab === t.key && !sub ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.icon className="h-5 w-5" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-3">
          <div className="mb-2 flex items-center justify-between px-2">
            <p className="text-sm font-medium text-slate-700">{user?.name}</p>
            <NotificationBell variant="light" />
          </div>
          <button onClick={onLogout} className="mt-2 flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" /> Çıkış Yap</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 pb-24 lg:pb-10">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-xl lg:hidden">
          {sub ? (
            <>
              <button onClick={nav.back} data-testid="sub-back" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100"><ChevronLeft className="h-5 w-5 text-slate-700" /></button>
              <span className="font-heading flex-1 font-semibold text-slate-900">{subTitle}</span>
              <NotificationBell variant="light" />
            </>
          ) : (
            <>
              <Logo className="h-7 w-auto flex-1 object-contain object-left" alt="RENKA" />
              <NotificationBell variant="light" />
            </>
          )}
        </div>

        {/* Desktop back bar for sub views */}
        {sub && (
          <div className="hidden px-8 pt-8 lg:block">
            <button onClick={nav.back} data-testid="sub-back-desktop" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800"><ChevronLeft className="h-4 w-4" /> Geri</button>
          </div>
        )}

        <div className="mx-auto max-w-2xl px-4 py-6 lg:px-8">
          {/* multi-child selector (non-home tabs) */}
          {students.length > 1 && !sub && tab !== "home" && tab !== "profile" && (
            <div className="mb-4 max-w-xs">
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="h-11 rounded-xl bg-white" data-testid="parent-student-select"><SelectValue /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {renderContent()}
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden">
        {TABS.map((t) => {
          const active = tab === t.key && !sub;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => nav.goTab(t.key)}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${active ? "text-violet-600" : "text-slate-400"}`}>
              <t.icon className={`h-5 w-5 ${active ? "text-violet-600" : "text-slate-400"}`} /> {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
